import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Receipt, Download, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  plan: "Abonelik",
  module: "Okul Modülü",
  credit_package: "Kredi Paketi",
  promote_exam: "Sınav Öne Çıkarma",
};

const STATUS_BADGE = {
  paid: { label: "Ödendi", cls: "bg-success/10 text-success", icon: CheckCircle2 },
  failed: { label: "Başarısız", cls: "bg-destructive/10 text-destructive", icon: XCircle },
  pending: { label: "Bekliyor", cls: "bg-muted text-muted-foreground", icon: Loader2 },
  refunded: { label: "İade Edildi", cls: "bg-amber-500/10 text-amber-600", icon: CheckCircle2 },
};

export default function PaymentHistory({ title = "Ödemelerim" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get(`${V1}/payments/iyzico/my`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const downloadInvoice = (paymentId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/payments/${paymentId}/invoice.pdf`;
    const token = localStorage.getItem("sano_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = `fatura-${paymentId.slice(0, 12)}.pdf`;
        link.click();
      })
      .catch(() => toast.error("Fatura indirilemedi"));
  };

  return (
    <div className="space-y-6" data-testid="payment-history-page">
      <div>
        <div className="overline text-accent">Ticaret</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Receipt className="h-7 w-7 text-primary" /> {title}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Tüm abonelik, modül ve kredi paketi ödemelerin ve faturaların burada.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Tarih</th>
              <th className="text-left p-3">Açıklama</th>
              <th className="text-left p-3">Tür</th>
              <th className="text-left p-3">Tutar</th>
              <th className="text-left p-3">Durum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items === null && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Yükleniyor…</td></tr>
            )}
            {items?.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Henüz bir ödemen yok.</td></tr>
            )}
            {items?.map((p) => {
              const badge = STATUS_BADGE[p.status] || STATUS_BADGE.pending;
              return (
                <tr key={p.id} className="border-t border-border" data-testid={`payment-row-${p.id}`}>
                  <td className="p-3 text-xs text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString("tr-TR")}
                  </td>
                  <td className="p-3 font-medium">{p.item_name || p.code}</td>
                  <td className="p-3 text-xs">{KIND_LABEL[p.kind] || p.kind}</td>
                  <td className="p-3 font-mono">₺{p.amount}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                      <badge.icon className="h-3 w-3" /> {badge.label}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {p.status === "paid" && (
                      <Button size="sm" variant="ghost" onClick={() => downloadInvoice(p.id)} data-testid={`download-invoice-${p.id}`}>
                        <Download className="h-3.5 w-3.5" /> Fatura
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
